import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUpRight, Lock, Pencil, Plus, Sparkles, Tags, Trash2 } from "lucide-react";
import { cn } from "@/lib/cn";
import { friendlyError } from "@/lib/errors";
import { useActiveOrg } from "@/features/tenancy/hooks";
import { Button } from "@/components/ui/Button";
import { Panel } from "@/components/ui/Panel";
import { StatusPill } from "@/components/ui/StatusPill";
import { Modal } from "@/components/ui/Modal";
import { QueryBoundary } from "@/components/ui/QueryBoundary";
import { EmptyState, Skeleton, TableSkeleton } from "@/components/ui/states";
import { toast } from "@/components/ui/toast";
import {
  ATTRIBUTE_TYPES,
  adoptPresets,
  createAttribute,
  deleteAttribute,
  fetchAttributes,
  fetchPresets,
  fetchQuota,
  needsOptions,
  slugifyKey,
  typeLabel,
  updateAttribute,
  type AttributeDraft,
  type AttributeRow,
} from "./attributesApi";

const VACIO: AttributeDraft = {
  label: "",
  dataType: "select",
  unit: "",
  options: [],
  appliesTo: "product",
  filterable: true,
  active: true,
};

export function AttributesPage() {
  const { org } = useActiveOrg();
  const qc = useQueryClient();
  const [editando, setEditando] = useState<AttributeRow | null>(null);
  const [creando, setCreando] = useState(false);
  const [recomendados, setRecomendados] = useState(false);
  const [borrando, setBorrando] = useState<AttributeRow | null>(null);

  const orgId = org?.organizationId ?? null;

  const quota = useQuery({
    queryKey: ["attribute-quota", orgId],
    queryFn: () => fetchQuota(orgId!),
    enabled: Boolean(orgId),
  });

  const lista = useQuery({
    queryKey: ["attributes", orgId],
    queryFn: () => fetchAttributes(orgId!),
    enabled: Boolean(orgId),
  });

  const refrescar = async () => {
    await qc.invalidateQueries({ queryKey: ["attributes"] });
    await qc.invalidateQueries({ queryKey: ["attribute-quota"] });
    await qc.invalidateQueries({ queryKey: ["spec-fields"] });
  };

  const q = quota.data;
  const ilimitado = q?.maxAttributes === null;
  const enLimite = Boolean(q && !ilimitado && q.used >= (q.maxAttributes ?? 0));

  return (
    <div className="flex flex-col gap-3">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-head font-semibold tracking-tight">Atributos</h1>
          <p className="text-ui text-ink-2">
            Se definen una vez y se reutilizan en todos los productos.
          </p>
        </div>
        <div className="flex gap-2">
          {org?.industry && (
            <Button
              variant="secondary"
              size="sm"
              icon={<Sparkles className="size-3.5" />}
              disabled={enLimite}
              onClick={() => setRecomendados(true)}
            >
              Recomendados
            </Button>
          )}
          <Button
            variant="primary"
            size="sm"
            icon={<Plus className="size-3.5" />}
            disabled={enLimite}
            onClick={() => setCreando(true)}
          >
            Crear atributo
          </Button>
        </div>
      </header>

      {q && <QuotaBar quota={q} enLimite={enLimite} />}

      <Panel bleed>
        <QueryBoundary
          query={lista}
          skeleton={<TableSkeleton rows={5} cols={5} />}
          isEmpty={(d) => d.length === 0}
          empty={
            <EmptyState
              icon={<Tags className="size-5" />}
              title="Aún no has configurado atributos"
              description="Los atributos son las características que describen tus productos: color, talla, material. Se crean aquí y se usan en todo el catálogo."
              actions={
                <Button size="sm" variant="primary" onClick={() => setCreando(true)}>
                  Crear el primero
                </Button>
              }
            />
          }
        >
          {(rows) => (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] border-collapse text-ui">
                <thead>
                  <tr>
                    {["Atributo", "Tipo", "Valores", "Productos", "Estado", ""].map((h, i) => (
                      <th
                        key={h || i}
                        className={cn(
                          "border-b border-line bg-sunk px-4 py-2.5 font-mono text-mark font-medium uppercase tracking-[0.1em] text-ink-3",
                          i === 2 || i === 3 ? "text-right" : "text-left",
                        )}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((a) => (
                    <tr key={a.id} className="border-b border-line-2 last:border-0">
                      <td className="px-4 py-3">
                        <span className="font-medium">{a.label}</span>
                        <span className="tnum block text-xs text-ink-3">
                          {a.key}
                          {a.applies_to === "variant" && " · por variante"}
                          {a.filterable && " · filtrable"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-ink-2">
                        {typeLabel(a.data_type)}
                        {a.unit && (
                          <span className="ml-1.5 font-mono text-mark text-ink-3">
                            {a.unit}
                          </span>
                        )}
                      </td>
                      <td className="tnum px-4 py-3 text-right text-ink-2">
                        {needsOptions(a.data_type) ? a.valuesCount : "—"}
                      </td>
                      <td className="tnum px-4 py-3 text-right text-ink-2">{a.productsUsed}</td>
                      <td className="px-4 py-3">
                        <StatusPill tone={a.active ? "ok" : "neutral"}>
                          {a.active ? "Activo" : "Inactivo"}
                        </StatusPill>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            aria-label={`Editar ${a.label}`}
                            onClick={() => setEditando(a)}
                          >
                            <Pencil className="size-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            aria-label={`Eliminar ${a.label}`}
                            onClick={() => setBorrando(a)}
                          >
                            <Trash2 className="size-3.5" />
                          </Button>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </QueryBoundary>
      </Panel>

      {(creando || editando) && orgId && (
        <AttributeDialog
          organizationId={orgId}
          attribute={editando}
          onClose={() => {
            setCreando(false);
            setEditando(null);
          }}
          onSaved={async () => {
            await refrescar();
            setCreando(false);
            setEditando(null);
          }}
        />
      )}

      {recomendados && orgId && (
        <PresetsDialog
          organizationId={orgId}
          industry={org?.industry ?? null}
          cupoLibre={q?.maxAttributes === null ? Infinity : (q?.maxAttributes ?? 0) - (q?.used ?? 0)}
          onClose={() => setRecomendados(false)}
          onAdopted={async () => {
            await refrescar();
            setRecomendados(false);
          }}
        />
      )}

      {borrando && (
        <Modal
          open
          tone="danger"
          title={`Eliminar ${borrando.label}`}
          onClose={() => setBorrando(null)}
          description={
            borrando.productsUsed > 0 ? (
              <>
                Este atributo está en{" "}
                <strong className="text-ink">{borrando.productsUsed} producto(s)</strong>. Al
                eliminarlo se borran también esos valores, y no se pueden recuperar. Si solo
                quieres dejar de usarlo, desactívalo: no ocupa cupo y los datos se conservan.
              </>
            ) : (
              "No está en uso en ningún producto."
            )
          }
          footer={
            <>
              <Button size="sm" variant="secondary" onClick={() => setBorrando(null)}>
                Volver
              </Button>
              <Button
                size="sm"
                variant="danger"
                onClick={async () => {
                  try {
                    await deleteAttribute(borrando.id);
                    toast.ok("Atributo eliminado");
                    await refrescar();
                    setBorrando(null);
                  } catch (e) {
                    toast.error(friendlyError(e));
                  }
                }}
              >
                Eliminar
              </Button>
            </>
          }
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------- cupo */

function QuotaBar({
  quota,
  enLimite,
}: {
  quota: NonNullable<Awaited<ReturnType<typeof fetchQuota>>>;
  enLimite: boolean;
}) {
  const ilimitado = quota.maxAttributes === null;
  const pct = ilimitado ? 0 : Math.min(100, (quota.used / (quota.maxAttributes ?? 1)) * 100);

  return (
    <div
      className={cn(
        "rounded-lg border p-4",
        enLimite ? "border-warn/40 bg-warn-soft" : "border-line bg-surface",
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-baseline gap-2">
          <span className="tnum text-emph font-semibold">
            {ilimitado ? `${quota.used} atributos` : `${quota.used} de ${quota.maxAttributes}`}
          </span>
          <span className="text-ui text-ink-2">
            Plan {quota.planName}
            {ilimitado && " · sin límite"}
          </span>
        </div>

        {enLimite && quota.nextPlanName && (
          <Button
            size="sm"
            variant="secondary"
            icon={<ArrowUpRight className="size-3.5" />}
            onClick={() =>
              toast.ok(
                `Escríbenos para pasar al plan ${quota.nextPlanName}. Te avisamos en cuanto esté activo.`,
              )
            }
          >
            Mejorar plan
          </Button>
        )}
      </div>

      {!ilimitado && (
        <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-line">
          <div
            className={cn("h-full rounded-full", enLimite ? "bg-warn" : "bg-accent")}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}

      {enLimite && (
        <p className="mt-3 flex items-start gap-2 text-ui text-warn">
          <Lock className="mt-0.5 size-3.5 shrink-0" />
          <span>
            Has alcanzado el límite de atributos de tu plan.
            {quota.nextPlanName && (
              <>
                {" "}
                El plan {quota.nextPlanName} permite{" "}
                {quota.nextPlanMax === null ? "atributos ilimitados" : `${quota.nextPlanMax}`}.
              </>
            )}{" "}
            También puedes desactivar uno que no uses: los inactivos no ocupan cupo y sus datos
            se conservan.
          </span>
        </p>
      )}
    </div>
  );
}

/* --------------------------------------------------- recomendados del rubro */

function PresetsDialog({
  organizationId,
  industry,
  cupoLibre,
  onClose,
  onAdopted,
}: {
  organizationId: string;
  industry: string | null;
  cupoLibre: number;
  onClose: () => void;
  onAdopted: () => Promise<void>;
}) {
  const [elegidos, setElegidos] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  const presets = useQuery({
    queryKey: ["presets", industry, organizationId],
    queryFn: () => fetchPresets(industry, organizationId),
  });

  const disponibles = presets.data ?? [];
  const excede = elegidos.size > cupoLibre;

  return (
    <Modal
      open
      onClose={onClose}
      title="Atributos recomendados"
      description="Los que suele usar tu rubro. Puedes renombrarlos o quitarles valores después."
      footer={
        <>
          <Button size="sm" variant="secondary" onClick={onClose}>
            Cerrar
          </Button>
          <Button
            size="sm"
            variant="primary"
            loading={busy}
            disabled={elegidos.size === 0 || excede}
            onClick={async () => {
              setBusy(true);
              try {
                const n = await adoptPresets(organizationId, [...elegidos]);
                toast.ok(`${n} atributo${n === 1 ? "" : "s"} agregado${n === 1 ? "" : "s"}`);
                await onAdopted();
              } catch (e) {
                toast.error(friendlyError(e));
              }
              setBusy(false);
            }}
          >
            Agregar {elegidos.size > 0 ? elegidos.size : ""}
          </Button>
        </>
      }
    >
      {excede && (
        <p className="mb-3 rounded-md bg-warn-soft px-3 py-2 text-meta text-warn">
          Solo te quedan {cupoLibre} en tu plan. Quita {elegidos.size - cupoLibre} de la
          selección.
        </p>
      )}

      {presets.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-8" />
          <Skeleton className="h-8" />
        </div>
      ) : disponibles.length === 0 ? (
        <p className="py-6 text-center text-ui text-ink-2">
          Ya tienes todos los recomendados de tu rubro.
        </p>
      ) : (
        <ul className="-mx-1 max-h-[46vh] overflow-y-auto">
          {disponibles.map((p) => {
            const on = elegidos.has(p.key);
            return (
              <li key={p.id}>
                <label
                  className={cn(
                    "flex cursor-pointer items-start gap-2.5 rounded-md px-1.5 py-2",
                    on ? "bg-accent-soft" : "hover:bg-sunk",
                  )}
                >
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() =>
                      setElegidos((s) => {
                        const n = new Set(s);
                        if (n.has(p.key)) n.delete(p.key);
                        else n.add(p.key);
                        return n;
                      })
                    }
                    className="mt-0.5 size-3.5 shrink-0 accent-[var(--accent)]"
                  />
                  <span className="min-w-0">
                    <span className="block text-ui font-medium">
                      {p.label}
                      {p.unit && (
                        <span className="ml-1.5 font-mono text-mark text-ink-3">{p.unit}</span>
                      )}
                    </span>
                    <span className="block text-xs text-ink-3">
                      {typeLabel(p.data_type)}
                      {p.applies_to === "variant" && " · por variante"}
                      {p.options.length > 0 &&
                        ` · ${p.options.slice(0, 3).join(", ")}${p.options.length > 3 ? "…" : ""}`}
                    </span>
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
}

/* --------------------------------------------------------- crear y editar */

function AttributeDialog({
  organizationId,
  attribute,
  onClose,
  onSaved,
}: {
  organizationId: string;
  attribute: AttributeRow | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [draft, setDraft] = useState<AttributeDraft>(VACIO);
  const [opciones, setOpciones] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (attribute) {
      setDraft({
        label: attribute.label,
        dataType: attribute.data_type,
        unit: attribute.unit ?? "",
        options: attribute.options,
        appliesTo: attribute.applies_to as "product" | "variant",
        filterable: attribute.filterable,
        active: attribute.active,
      });
      setOpciones(attribute.options.join("\n"));
    } else {
      setDraft(VACIO);
      setOpciones("");
    }
  }, [attribute]);

  const lista = opciones
    .split("\n")
    .map((o) => o.trim())
    .filter(Boolean);

  const requiereOpciones = needsOptions(draft.dataType);
  const valido = draft.label.trim().length > 1 && (!requiereOpciones || lista.length > 0);

  const set = <K extends keyof AttributeDraft>(k: K, v: AttributeDraft[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  return (
    <Modal
      open
      onClose={onClose}
      title={attribute ? `Editar ${attribute.label}` : "Crear atributo"}
      description={
        attribute
          ? "Cambiar el tipo o quitar valores puede dejar sin efecto lo ya cargado en los productos."
          : "Se define una vez y se reutiliza en todos los productos que lo necesiten."
      }
      footer={
        <>
          <Button size="sm" variant="secondary" onClick={onClose}>
            Volver
          </Button>
          <Button
            size="sm"
            variant="primary"
            loading={busy}
            disabled={!valido}
            onClick={async () => {
              setBusy(true);
              try {
                const payload = { ...draft, options: lista };
                if (attribute) await updateAttribute(attribute.id, payload);
                else await createAttribute(organizationId, payload);
                toast.ok(attribute ? "Atributo actualizado" : "Atributo creado");
                await onSaved();
              } catch (e) {
                toast.error(friendlyError(e));
              }
              setBusy(false);
            }}
          >
            {attribute ? "Guardar" : "Crear atributo"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3.5">
        <label className="flex flex-col gap-1.5">
          <span className="text-meta font-medium">Nombre</span>
          <input
            value={draft.label}
            onChange={(e) => set("label", e.target.value)}
            placeholder="Color, Talla, Material…"
            className="w-full rounded-md border border-line bg-surface px-3 py-2 text-ui"
          />
          {draft.label.trim() && !attribute && (
            <span className="font-mono text-mark text-ink-3">{slugifyKey(draft.label)}</span>
          )}
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-meta font-medium">Tipo</span>
          <select
            value={draft.dataType}
            onChange={(e) => set("dataType", e.target.value)}
            className="w-full rounded-md border border-line bg-surface px-3 py-2 text-ui"
          >
            {ATTRIBUTE_TYPES.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
        </label>

        {draft.dataType === "number" && (
          <label className="flex flex-col gap-1.5">
            <span className="text-meta font-medium">Unidad</span>
            <input
              value={draft.unit ?? ""}
              onChange={(e) => set("unit", e.target.value)}
              placeholder="cm, kg, GB, V…"
              className="w-full rounded-md border border-line bg-surface px-3 py-2 text-ui"
            />
            <span className="text-xs text-ink-3">
              Se guarda solo el número: así se puede ordenar y filtrar por rango.
            </span>
          </label>
        )}

        {requiereOpciones && (
          <label className="flex flex-col gap-1.5">
            <span className="text-meta font-medium">Valores disponibles</span>
            <textarea
              rows={5}
              value={opciones}
              onChange={(e) => setOpciones(e.target.value)}
              placeholder={"Negro\nBlanco\nAzul\nRojo"}
              className="w-full rounded-md border border-line bg-surface px-3 py-2 text-ui"
            />
            <span className="text-xs text-ink-3">
              Uno por línea. Solo se podrán elegir estos, y es lo que mantiene los filtros de la
              tienda funcionando.
            </span>
          </label>
        )}

        <label className="flex flex-col gap-1.5">
          <span className="text-meta font-medium">Nivel</span>
          <select
            value={draft.appliesTo}
            onChange={(e) => set("appliesTo", e.target.value as "product" | "variant")}
            className="w-full rounded-md border border-line bg-surface px-3 py-2 text-ui"
          >
            <option value="product">Igual para todo el producto</option>
            <option value="variant">Distinto en cada variante</option>
          </select>
        </label>

        <div className="flex flex-col gap-2 border-t border-line-2 pt-3">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={draft.filterable}
              onChange={(e) => set("filterable", e.target.checked)}
              className="size-3.5 accent-[var(--accent)]"
            />
            <span className="text-ui">Usar como filtro en la tienda</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={draft.active}
              onChange={(e) => set("active", e.target.checked)}
              className="size-3.5 accent-[var(--accent)]"
            />
            <span className="text-ui">
              Activo
              <span className="ml-1.5 text-xs text-ink-3">los inactivos no ocupan cupo</span>
            </span>
          </label>
        </div>
      </div>
    </Modal>
  );
}
